/*
 *************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 *************************************************************************
 */

import './Skeleton.scss';

export interface SkeletonWrapperProps {
	className: string;
	children: string;
}

export interface SkeletonHeadingProps {
	lines?: number;
	level?: 1 | 2 | 3 | 4 | 5 | 6;
	align?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonRectProps {
	width?: string | number;
	height?: string | number;
}

export interface SkeletonParagraphProps {
	lines?: number;
	align?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonCaptionProps {
	lines?: number;
	align?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonImageProps {
	aspect?:
		| '1/1'
		| '16/9'
		| '9/16'
		| '21/9'
		| '9/21'
		| '4/3'
		| '3/4'
		| '3/5'
		| '5/3';
	borderRadius?: string;
}

export interface SkeletonButtonsProps {
	count?: number;
	type?: 'button' | 'action' | 'icon';
	justify?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonResponsiveValue<
	T extends string | number = string | number,
> {
	base?: T;
	S?: T;
	M?: T;
	L?: T;
	XL?: T;
	XXL?: T;
	HOMEV2_M?: T;
	HOMEV2_L?: T;
	HOMEV2_XL?: T;
}

export class Skeleton {
	static wrapper(props: SkeletonWrapperProps): string {
		const {className, children} = props;
		return `<div class="${className} skl-wrapper" aria-hidden="true" role="presentation">${children}</div>`;
	}

	static repeat(count: number, fill: string): string {
		return Array(count).fill(fill).join('');
	}

	static parseResponsiveClasses<T extends SkeletonResponsiveValue>(
		element: string,
		modifier: string,
		value: T,
	): string {
		const {base, S, M, L, XL, XXL, HOMEV2_M, HOMEV2_L, HOMEV2_XL} = value;
		const classes: string[] = [];
		if (base) {
			classes.push(`${element}--${modifier}-${base}`);
		}
		if (S) {
			classes.push(`${element}--S-${modifier}-${S}`);
		}
		if (M) {
			classes.push(`${element}--M-${modifier}-${M}`);
		}
		if (L) {
			classes.push(`${element}--L-${modifier}-${L}`);
		}
		if (XL) {
			classes.push(`${element}--XL-${modifier}-${XL}`);
		}
		if (XXL) {
			classes.push(`${element}--XXL-${modifier}-${XXL}`);
		}
		if (HOMEV2_M) {
			classes.push(`${element}--HOMEV2_M-${modifier}-${HOMEV2_M}`);
		}
		if (HOMEV2_L) {
			classes.push(`${element}--HOMEV2_L-${modifier}-${HOMEV2_L}`);
		}
		if (HOMEV2_XL) {
			classes.push(`${element}--HOMEV2_XL-${modifier}-${HOMEV2_XL}`);
		}
		return classes.join(' ');
	}

	static heading(props: SkeletonHeadingProps = {}): string {
		const {lines = 1, level = 1, align = {base: 'start'}} = props;
		const alignClasses = this.parseResponsiveClasses(
			'skl-heading',
			'align',
			align,
		);
		return `
			<div class="skl-heading ${alignClasses}">
				${this.repeat(lines, `<div class="skl-heading-line skl-heading-line--level-${level}"></div>`)}
			</div>
		`;
	}

	static paragraph(props: SkeletonParagraphProps = {}): string {
		const {lines = 1, align = {base: 'start'}} = props;
		const alignClasses = this.parseResponsiveClasses(
			'skl-paragraph',
			'align',
			align,
		);
		return `
			<div class="skl-paragraph ${alignClasses}">
				${this.repeat(lines, `<div class="skl-paragraph-line"></div>`)}
			</div>
		`;
	}

	static caption(props: SkeletonCaptionProps = {}): string {
		const {lines = 1, align = {base: 'start'}} = props;
		const alignClasses = this.parseResponsiveClasses(
			'skl-caption',
			'align',
			align,
		);
		return `
			<div class="skl-caption ${alignClasses}">
				${this.repeat(lines, `<div class="skl-caption-line"></div>`)}
			</div>
		`;
	}

	static image(props: SkeletonImageProps = {}): string {
		const {aspect, borderRadius = '12px'} = props;
		return `<div class="skl-image" style="${aspect ? `aspect-ratio:${aspect};` : 'width:100%;height:100%;'}border-radius:${borderRadius}"></div>`;
	}

	static circle(): string {
		return '<div class="skl-circle"></div>';
	}

	static rect(props: SkeletonRectProps = {}): string {
		const {width = '100%', height = '100%'} = props;
		return `<div class="skl-rect" style="width:${width};height:${height};"></div>`;
	}

	static buttons(props: SkeletonButtonsProps = {}): string {
		const {count = 1, type = 'button', justify = {base: 'start'}} = props;
		const justifyClasses = this.parseResponsiveClasses(
			'skl-buttons',
			'justify',
			justify,
		);
		return `
			<div class="skl-buttons ${justifyClasses}">
				${this.repeat(count, `<div class="skl-button skl-button--type-${type}"></div>`)}
			</div>
		`;
	}

	static toString(): string {
		return this.wrapper({
			className: 'skl-default',
			children:
				'To use the skeleton utility, extend the "Skeleton" class, and override the static <code>.toString()</code> method.',
		});
	}
}
